import { useCallback, useContext, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { AllocationName, FinancialStatementSmall } from 'api';
import { COMPANY_TYPE } from 'common/constants/pageTypes';
import { COMPANY_NAME_TITLE } from 'common/constants/summary';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { useDocuments } from 'services/hooks';
import {
  useGetAllocationsVersionsByCompanyIdByDateId,
  useGetVersionsByCompanyIdByDateId,
  useHasCompanySummary,
} from 'services/hooks/company';
import { useListByMeasurementPeriod } from 'services/hooks/financialStatement';
import { Logger, useUserUtils } from 'utilities';
import { getMainSelectionForDropdown, parseDataForDropDowns, useGenerateReports } from '../../ExcelExportDialog/utils';
import { BaseExcelExportDialog } from '../BaseExcelExportDialog';
import { OtherOptionsRenderer, SubOptionsRenderer } from './components';
import { useExcelExportDataLoading, useExcelExportForm, useExcelExportValidation } from '../hooks';
import { BaseDialogProps, ExcelExportContextType, ExcelExportFormValues } from '../types';
import { isFormValid } from '../validations';
import {
  CompanyCurrentSelectedValues,
  CompanyExcelExportDialogErrors,
  EXPORT_OPTIONS,
  ExportOption,
  HasDocumentsData,
  HasSummaryData,
  OtherOptions,
  SelectedVersions,
} from './CompanyExcelExportDialog.types';

export const CompanyExcelExportDialog = ({ open, onClose }: BaseDialogProps) => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { currentMeasurementDate, pageType } = useContext(LayoutContext) as unknown as LayoutContextValues;
  const {
    isLoading: isExportLoading,
    currentSelectedValues: valuesSelectedExternally,
    setCurrentSelectedValues,
  } = useContext(ExcelExportContext) as unknown as ExcelExportContextType;
  const getAllocationVersions = useGetAllocationsVersionsByCompanyIdByDateId();
  const getCapTableVersions = useGetVersionsByCompanyIdByDateId();
  const hasCompanySummary = useHasCompanySummary();
  const [listFinancialByMeasurementDate] = useListByMeasurementPeriod();
  const { hasDocuments: checkHasDocuments } = useDocuments();

  const [selectedOption, setSelectedOption] = useState<ExportOption>(EXPORT_OPTIONS.VALUATION);
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [formulated, setFormulated] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [valuations, setValuations] = useState<{ id?: number; name?: string; is_main: boolean }[]>([]);
  const [captables, setCaptables] = useState<{ id?: number; name?: string; is_main: boolean }[]>([]);
  const [financials, setFinancials] = useState<{ id?: number; name?: string; is_main: boolean }[]>([]);
  const [hasSummary, setHasSummary] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(false);

  const [otherOptions, setOtherOptions] = useState<OtherOptions>({
    summary: false,
    capTable: false,
    fundOwnership: false,
    breakpointAnalysis: false,
    waterfallModel: false,
    financials: false,
    valuations: false,
    documents: false,
  });

  const [selectedVersions, setSelectedVersions] = useState<SelectedVersions>({
    capTable: null,
    waterfallModel: null,
    financials: null,
  });

  const currentSelectedValues = valuesSelectedExternally;
  const { name: company_name, legal_company_name: legalCompanyName } = companyInfo ?? {};
  const companyName = company_name || legalCompanyName || COMPANY_NAME_TITLE;
  const { isStaffUser } = useUserUtils();

  const initialFormValues: ExcelExportFormValues = {
    export_breakpoints: false,
    export_cap_table: false,
    export_documents: false,
    export_financials: false,
    export_firm_funds_summaries: false,
    export_firm_summary: false,
    export_fund_investments: false,
    export_fund_ownership: false,
    export_fund_summary: false,
    export_summary: false,
    export_valuations: false,
    export_waterfall: false,
    export_not_formulated: !formulated,
    export_formulated_and_print_ready: false,
    include_documents_and_notes_from_tabs: includeDocuments,
    breakpoint_cap_table: null,
    cap_table: null,
    financial_statement: null,
    fo_cap_table: null,
    valuation: selectedVersion,
    waterfall_cap_table: null,
    fund_summary: null,
    fund_investments: null,
    export_market_adjustment: false,
    market_adjustment: null,
    fund_investment_ownership: null,
    report_date: null,
  };

  const { formValues, updateFormValues } = useExcelExportForm({
    initialFormValues,
  });

  const { errors, setErrors } = useExcelExportValidation<CompanyExcelExportDialogErrors>({
    initialErrors: {} as CompanyExcelExportDialogErrors,
  });

  const { isLoading, dataLoadError, loadData } = useExcelExportDataLoading({
    isOpen: open,
    onError: error => Logger.error('Data loading error:', error),
  });

  // Reset state when dialog is opened to ensure fresh start
  useEffect(() => {
    if (open) {
      // Reset form values to initial state
      updateFormValues(initialFormValues);
      // Clear any previous errors
      setErrors({} as CompanyExcelExportDialogErrors);

      // Reset other state variables
      setSelectedOption(EXPORT_OPTIONS.VALUATION);
      setIncludeDocuments(true);
      setFormulated(true);
      setSelectedVersion(null);
      setOtherOptions({
        summary: false,
        capTable: false,
        fundOwnership: false,
        breakpointAnalysis: false,
        waterfallModel: false,
        financials: false,
        valuations: false,
        documents: false,
      });
      setSelectedVersions({
        capTable: null,
        waterfallModel: null,
        financials: null,
      });
    }
    // Disabling exhaustive deps to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updateFormValues, setErrors]);

  useEffect(() => {
    updateFormValues({
      export_cap_table: Boolean(captables.length > 0 && currentSelectedValues?.cap_table),
      export_summary: Boolean(hasSummary),
      export_breakpoints: Boolean(captables.length > 0 && currentSelectedValues?.breakpoint_cap_table),
      export_waterfall: Boolean(captables.length > 0 && currentSelectedValues?.waterfall_cap_table),
      export_fund_ownership: Boolean(captables.length > 0 && currentSelectedValues?.fo_cap_table),
      export_financials: Boolean(financials.length > 0 && currentSelectedValues?.financial_statement),
      export_valuations: Boolean(valuations.length > 0 && currentSelectedValues?.valuation),
      export_documents: Boolean(hasDocuments),
      export_not_formulated: !formulated,
      include_documents_and_notes_from_tabs: includeDocuments,
      valuation: selectedVersion || currentSelectedValues?.valuation || null,
      cap_table: currentSelectedValues?.cap_table || null,
      fo_cap_table: currentSelectedValues?.fo_cap_table || null,
      breakpoint_cap_table: currentSelectedValues?.breakpoint_cap_table || null,
      waterfall_cap_table: currentSelectedValues?.waterfall_cap_table || null,
      financial_statement: currentSelectedValues?.financial_statement || null,
    });
  }, [
    captables.length,
    hasSummary,
    financials.length,
    valuations.length,
    hasDocuments,
    formulated,
    includeDocuments,
    selectedVersion,
    currentSelectedValues,
    updateFormValues,
  ]);

  const { generateReport, generateCompanySummaryReport } = useGenerateReports({
    pageType,
    firmInfo: null,
    companyInfo,
    fundInfo: null,
    currentMeasurementDateId: currentMeasurementDate?.id ?? null,
    setErrors: setErrors as any,
    formValues,
  });

  const handleExport = useCallback(async () => {
    if (selectedOption === EXPORT_OPTIONS.VALUATION && selectedVersion) {
      await generateReport(formValues);
    } else if (selectedOption === EXPORT_OPTIONS.VALUATION_SUMMARY && selectedVersion) {
      await generateCompanySummaryReport();
    } else if (selectedOption === EXPORT_OPTIONS.OTHER) {
      const {
        summary: export_summary,
        capTable: export_cap_table,
        fundOwnership: export_fund_ownership,
        breakpointAnalysis: export_breakpoints,
        waterfallModel: export_waterfall,
        financials: export_financials,
        valuations: export_valuations,
        documents: export_documents,
      } = otherOptions;

      const { capTable, waterfallModel, financials: financial_statement } = selectedVersions;

      const otherFormValues = {
        export_summary,
        export_cap_table,
        export_fund_ownership,
        export_breakpoints,
        export_waterfall,
        export_financials,
        export_valuations,
        export_documents,
        cap_table: capTable,
        fo_cap_table: capTable,
        breakpoint_cap_table: capTable,
        waterfall_cap_table: waterfallModel,
        financial_statement,
        valuation: selectedVersion,
        export_not_formulated: !formulated,
        export_formulated_and_print_ready: false,
        include_documents_and_notes_from_tabs: includeDocuments,
      };
      await generateReport(otherFormValues);
    }
  }, [
    selectedOption,
    selectedVersion,
    formValues,
    generateReport,
    generateCompanySummaryReport,
    otherOptions,
    selectedVersions,
    formulated,
    includeDocuments,
  ]);

  const hasAtLeastOneReport =
    hasSummary || hasDocuments || captables.length > 0 || financials.length > 0 || valuations.length > 0;
  const hasAtLeastOneOtherOption = Object.values(otherOptions).some(Boolean);

  const isValidExportSelection =
    isFormValid(errors) &&
    hasAtLeastOneReport &&
    ((selectedOption === EXPORT_OPTIONS.VALUATION && selectedVersion) ||
      (selectedOption === EXPORT_OPTIONS.VALUATION_SUMMARY && selectedVersion) ||
      (selectedOption === EXPORT_OPTIONS.OTHER && hasAtLeastOneOtherOption));

  const handleVersionChange = useCallback((event: SelectChangeEvent<unknown>) => {    
    const newVersion = event.target.value as number;
    setSelectedVersion(newVersion);    
    setCurrentSelectedValues({
      ...currentSelectedValues,
      valuation: newVersion,
    });
  }, [currentSelectedValues, setCurrentSelectedValues]);

  const handleOtherOptionChange = useCallback((option: string) => {
    setOtherOptions(prev => ({
      ...prev,
      [option]: !prev[option as keyof typeof prev],
    }));
  }, []);

  const handleOtherVersionChange = useCallback((type: string, value: number) => {
    setSelectedVersions(prev => ({
      ...prev,
      [type]: value,
    }));
  }, []);

  const isExportDisabled = isLoading || isExportLoading || !isValidExportSelection;

  const fetchAllocationVersions = async (companyId: number, measurementDateId: number): Promise<AllocationName[]> => {
    try {
      const result = await getAllocationVersions(companyId, measurementDateId);
      return Array.isArray(result) ? (result as AllocationName[]) : [];
    } catch (error) {
      Logger.error('Error loading allocation versions', error);
      return [];
    }
  };

  const fetchCapTableVersions = async (companyId: number, measurementDateId: number): Promise<CapTableVersion[]> => {
    try {
      const result = await getCapTableVersions(companyId, measurementDateId);
      return Array.isArray(result) ? (result as CapTableVersion[]) : [];
    } catch (error) {
      Logger.error('Error loading cap table versions', error);
      return [];
    }
  };

  const fetchFinancialStatementVersions = async (measurementDateId: number): Promise<FinancialStatementSmall[]> => {
    try {
      const result = await listFinancialByMeasurementDate(measurementDateId);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      Logger.error('Error loading financial statement versions', error);
      return [];
    }
  };

  const fetchSummaryData = async (companyId: number, measurementDateId: number): Promise<HasSummaryData> => {
    try {
      const result = await hasCompanySummary(companyId, measurementDateId);
      return (result as HasSummaryData) || { has_company_summary: false };
    } catch (error) {
      Logger.error('Error loading company summary', error);
      return { has_company_summary: false };
    }
  };

  const fetchDocumentsData = async (companyId: number, measurementDateId: number): Promise<HasDocumentsData> => {
    try {
      const result = await checkHasDocuments(companyId, measurementDateId);
      return (result as HasDocumentsData) || { has_documents: false };
    } catch (error) {
      Logger.error('Error loading documents', error);
      return { has_documents: false };
    }
  };

  const parseCompanyData = (
    allocationVersions: AllocationName[],
    capTableVersions: CapTableVersion[],
    financialStatementVersion: FinancialStatementSmall[]
  ) => {
    const {
      captables: parsedCaptables,
      financial_statements: parsedFinancials,
      valuations: parsedValuations,
    } = parseDataForDropDowns(capTableVersions, financialStatementVersion, allocationVersions);

    return {
      parsedCaptables,
      parsedFinancials,
      parsedValuations,
    };
  };

  const setCompanyState = (
    hasSummaryData: HasSummaryData,
    hasDocumentsData: HasDocumentsData,
    parsedData: {
      parsedCaptables: { id?: number; name?: string; is_main: boolean }[];
      parsedFinancials: { id?: number; name?: string; is_main: boolean }[];
      parsedValuations: { id?: number; name?: string; is_main: boolean }[];
    }
  ) => {
    setHasSummary(hasSummaryData?.has_company_summary || false);
    setHasDocuments(hasDocumentsData?.has_documents || false);

    setValuations(parsedData.parsedValuations);
    setCaptables(parsedData.parsedCaptables);
    setFinancials(parsedData.parsedFinancials);

    const mainVersion = getMainSelectionForDropdown(parsedData.parsedValuations);
    setSelectedVersion(mainVersion);

    const capTableMain = getMainSelectionForDropdown(parsedData.parsedCaptables);
    const financialMain = getMainSelectionForDropdown(parsedData.parsedFinancials);

    setSelectedVersions(prev => ({
      ...prev,
      capTable: capTableMain,
      waterfallModel: capTableMain,
      financials: financialMain,
    }));
  };

  const setContextValues = (
    parsedData: {
      parsedCaptables: { id?: number; name?: string; is_main: boolean }[];
      parsedFinancials: { id?: number; name?: string; is_main: boolean }[];
      parsedValuations: { id?: number; name?: string; is_main: boolean }[];
    },
    capTableVersions: CapTableVersion[],
    financialStatementVersion: FinancialStatementSmall[]
  ) => {
    const contextValues: Partial<CompanyCurrentSelectedValues> = {};

    const mainVersion = getMainSelectionForDropdown(parsedData.parsedValuations);
    if (mainVersion) {
      contextValues.valuation = mainVersion;
    }

    if (capTableVersions.length > 0) {
      const capTableMainVersion = getMainSelectionForDropdown(parsedData.parsedCaptables);
      if (capTableMainVersion) {
        contextValues.cap_table = capTableMainVersion;
        contextValues.fo_cap_table = capTableMainVersion;
        contextValues.breakpoint_cap_table = capTableMainVersion;
        contextValues.waterfall_cap_table = capTableMainVersion;
      }
    }

    if (financialStatementVersion.length > 0) {
      const financialMainVersion = getMainSelectionForDropdown(parsedData.parsedFinancials);
      if (financialMainVersion) {
        contextValues.financial_statement = financialMainVersion;
      }
    }

    setCurrentSelectedValues(contextValues);
  };

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!open || !companyInfo?.id || !currentMeasurementDate?.id || pageType !== COMPANY_TYPE) {
        return;
      }

      await loadData(async () => {
        // Fetch all data
        const [allocationVersions, capTableVersions, financialStatementVersion, hasSummaryData, hasDocumentsData] =
          await Promise.all([
            fetchAllocationVersions(companyInfo.id!, currentMeasurementDate.id!),
            fetchCapTableVersions(companyInfo.id!, currentMeasurementDate.id!),
            fetchFinancialStatementVersions(currentMeasurementDate.id!),
            fetchSummaryData(companyInfo.id!, currentMeasurementDate.id!),
            fetchDocumentsData(companyInfo.id!, currentMeasurementDate.id!),
          ]);

        // Parse data
        const parsedData = parseCompanyData(allocationVersions, capTableVersions, financialStatementVersion);

        // Set component state
        setCompanyState(hasSummaryData, hasDocumentsData, parsedData);

        // Set context values
        setContextValues(parsedData, capTableVersions, financialStatementVersion);
      });
    };

    loadCompanyData();
    // Disabling exhaustive deps to prevent infinite re-renders and unnecessary API calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyInfo?.id, currentMeasurementDate?.md_id, pageType]);

  const title = `${companyName} Excel Export`;

  const renderSubOptions = (currentSelectedOption: string) => (
    <SubOptionsRenderer
      currentSelectedOption={currentSelectedOption}
      selectedVersion={selectedVersion}
      valuations={valuations}
      isLoading={isLoading}
      includeDocuments={includeDocuments}
      formulated={formulated}
      isStaffUser={isStaffUser}
      onVersionChange={handleVersionChange}
      onIncludeDocumentsChange={setIncludeDocuments}
      onFormulatedChange={setFormulated}
    />
  );

  const handleSetSelectedOption = useCallback(
    (option: string) => {
      setSelectedOption(option as ExportOption);
    },
    [setSelectedOption]
  );

  const handleSetOtherOptions = useCallback(
    (options: Record<string, boolean>) => {
      setOtherOptions(options as OtherOptions);
    },
    [setOtherOptions]
  );

  const renderOtherOptions = (currentSelectedOption: string) => (
    <OtherOptionsRenderer
      currentSelectedOption={currentSelectedOption}
      otherOptions={otherOptions}
      selectedVersions={selectedVersions}
      hasSummary={hasSummary}
      hasDocuments={hasDocuments}
      captables={captables}
      financials={financials}
      valuations={valuations}
      onOtherOptionChange={handleOtherOptionChange}
      onOtherVersionChange={handleOtherVersionChange}
    />
  );

  return (
    <BaseExcelExportDialog
      open={open}
      onClose={onClose}
      title={title}
      selectedOption={selectedOption}
      setSelectedOption={handleSetSelectedOption}
      formulated={formulated}
      setFormulated={setFormulated}
      otherOptions={otherOptions}
      setOtherOptions={handleSetOtherOptions}
      hasAtLeastOneReport={hasAtLeastOneReport}
      hasAtLeastOneOtherOption={hasAtLeastOneOtherOption}
      isOtherOptionValid
      errors={errors}
      setErrors={setErrors}
      dataLoadError={dataLoadError}
      isLoading={isLoading}
      isExportDisabled={isExportDisabled}
      isExportLoading={isExportLoading}
      onExport={handleExport}
      renderSubOptions={renderSubOptions}
      renderOtherOptions={renderOtherOptions}
    />
  );
};
